import React, { useState, useEffect } from 'react';
import { BannerSet, DataFeed } from '../types';
import { X, Save, Settings, FileText, Link, AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { v4 as uuidv4 } from 'uuid';
import { BANNER_SIZES } from '../constants/bannerSizes';

interface AddBannerSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddBannerSet: (bannerSet: BannerSet) => void;
  clientName: string;
}

export const AddBannerSetModal: React.FC<AddBannerSetModalProps> = ({
  isOpen,
  onClose,
  onAddBannerSet,
  clientName
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    jsonFeedUrl: '',
    status: 'draft' as BannerSet['status']
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [urlValidation, setUrlValidation] = useState<{
    isValid: boolean;
    message: string;
    bannerCount?: number;
  } | null>(null);
  
  const { showSuccess, showError, showInfo } = useToast();

  if (!isOpen) return null;

  const validateJsonUrl = async (url: string): Promise<{
    isValid: boolean;
    message: string;
    bannerCount?: number;
  }> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'DealerCentives-BannerEditor/1.0'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('URL does not return JSON data');
      }
      
      const jsonData = await response.json();
      console.log('✅ JSON feed loaded:', {
        keys: Object.keys(jsonData),
        totalSize: JSON.stringify(jsonData).length
      });
      
      // Parse different JSON structures
      let rawItems: any[] = [];
      
      if (jsonData.banners && Array.isArray(jsonData.banners)) {
        rawItems = jsonData.banners;
      } else if (jsonData.vehicles && Array.isArray(jsonData.vehicles)) {
        rawItems = jsonData.vehicles;
      } else if (jsonData.inventory && Array.isArray(jsonData.inventory)) {
        rawItems = jsonData.inventory;
      } else if (jsonData.data && Array.isArray(jsonData.data)) {
        rawItems = jsonData.data;
      } else if (Array.isArray(jsonData)) {
        rawItems = jsonData;
      } else {
        throw new Error('JSON feed does not contain a valid array of items');
      }
      
      if (rawItems.length === 0) {
        throw new Error('JSON feed contains no items');
      }
      
      return {
        isValid: true,
        message: `✅ Valid JSON feed with ${rawItems.length} items`,
        bannerCount: rawItems.length
      };
      
    } catch (error: any) {
      console.error('❌ Error validating JSON URL:', error);
      
      if (error.name === 'AbortError') {
        return {
          isValid: false,
          message: '❌ Request timed out - please check the URL and try again'
        };
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          isValid: false,
          message: '❌ Unable to connect to the URL - please check the URL and CORS settings'
        };
      }
      
      return {
        isValid: false,
        message: `❌ ${error.message}`
      };
    }
  };

  // Debounced URL validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.jsonFeedUrl) {
        setIsValidatingUrl(true);
        validateJsonUrl(formData.jsonFeedUrl)
          .then(result => {
            setUrlValidation(result);
          })
          .catch(error => {
            setUrlValidation({
              isValid: false,
              message: `❌ ${error.message}`
            });
          })
          .finally(() => {
            setIsValidatingUrl(false);
          });
      } else {
        setUrlValidation(null);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [formData.jsonFeedUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showError('Validation Error', 'Please enter a banner set name');
      return;
    }

    if (!formData.jsonFeedUrl.trim()) {
      showError('Validation Error', 'Please enter a JSON feed URL');
      return;
    }

    if (!urlValidation?.isValid) {
      showError('Invalid JSON URL', 'Please provide a valid JSON feed URL');
      return;
    }

    setIsSubmitting(true);

    try {
      // Fetch and validate JSON data
      const jsonData = await fetch(formData.jsonFeedUrl).then(res => res.json());
      
      // Parse different JSON structures
      let rawItems: any[] = [];
      
      if (jsonData.banners && Array.isArray(jsonData.banners)) {
        rawItems = jsonData.banners;
      } else if (jsonData.vehicles && Array.isArray(jsonData.vehicles)) {
        rawItems = jsonData.vehicles;
      } else if (jsonData.inventory && Array.isArray(jsonData.inventory)) {
        rawItems = jsonData.inventory;
      } else if (jsonData.data && Array.isArray(jsonData.data)) {
        rawItems = jsonData.data;
      } else if (Array.isArray(jsonData)) {
        rawItems = jsonData;
      }

      // Convert feed items to banner format
      const dataFeedArray: DataFeed[] = rawItems.slice(0, 50).map((item, index) => ({
        vehicle_year: item.year || new Date().getFullYear().toString(),
        model: item.model || item.name || `Vehicle ${index + 1}`,
        price: item.price?.toString() || item.msrp?.toString() || '0',
        term: item.term || '36',
        due_at_signing: item.due_at_signing || '0',
        apr: item.apr?.toString() || '0',
        apr_term: item.apr_term || '36',
        image_url: item.image || item.photo || item.imageUrl || '',
        disclaimer: item.disclaimer || '',
        ...item // Include all other fields
      }));

      // Create banner sizes with empty elements
      const bannerSizes = BANNER_SIZES.map(size => ({
        ...size,
        elements: [],
        lastModified: new Date()
      }));

      const newBannerSet: BannerSet = {
        id: uuidv4(),
        name: formData.name.trim(),
        description: formData.description.trim(),
        sizes: bannerSizes,
        dataFeed: dataFeedArray[0] || null,
        dataFeedArray: dataFeedArray,
        jsonFeedUrl: formData.jsonFeedUrl.trim(),
        createdAt: new Date(),
        lastModified: new Date(),
        status: formData.status
      };

      onAddBannerSet(newBannerSet);
      showSuccess('Banner Set Created', `${newBannerSet.name} has been successfully created with ${dataFeedArray.length} data items`);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        jsonFeedUrl: '',
        status: 'draft'
      });
      setUrlValidation(null);
      
      onClose();
    } catch (error: any) {
      console.error('Failed to create banner set:', error);
      showError('Error', 'Failed to create banner set. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const statusOptions = [
    { value: 'draft', label: 'Draft', description: 'Work in progress, not visible to others', color: 'yellow' },
    { value: 'active', label: 'Active', description: 'Live and ready for use', color: 'green' },
    { value: 'archived', label: 'Archived', description: 'Completed or no longer in use', color: 'gray' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'border-green-500 bg-green-50';
      case 'archived': return 'border-gray-500 bg-gray-50';
      default: return 'border-yellow-500 bg-yellow-50';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Settings className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create New Banner Set</h2>
              <p className="text-sm text-gray-600">For {clientName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Set Name *
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="e.g., Holiday Specials 2025, Spring Sale Campaign"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  placeholder="Describe the purpose and goals of this banner set..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  JSON Feed URL *
                </label>
                <div className="relative">
                  <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="url"
                    value={formData.jsonFeedUrl}
                    onChange={(e) => handleInputChange('jsonFeedUrl', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="https://example.com/inventory.json"
                    required
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter the URL of your JSON feed containing vehicle inventory data
                </p>
                
                {/* URL Validation Feedback */}
                {isValidatingUrl && (
                  <div className="mt-2 flex items-center text-sm text-gray-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Validating JSON feed...
                  </div>
                )}
                
                {urlValidation && (
                  <div className={`mt-2 flex items-center text-sm ${
                    urlValidation.isValid ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {urlValidation.isValid ? (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    ) : (
                      <AlertCircle className="h-4 w-4 mr-2" />
                    )}
                    {urlValidation.message}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Initial Status</h3>
            <div className="space-y-3">
              {statusOptions.map((status) => (
                <label
                  key={status.value}
                  className={`flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                    formData.status === status.value ? getStatusColor(status.value) : ''
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={status.value}
                    checked={formData.status === status.value}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={isSubmitting}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{status.label}</div>
                    <div className="text-sm text-gray-600">{status.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isValidatingUrl || !urlValidation?.isValid}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Create Banner Set</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};